const A4_WIDTH_MM = 210;
const A4_HEIGHT_MM = 297;
const ATOL_BACKGROUND_RGB = [255, 251, 210] as const;
const CANVAS_SCALE = 2.0;
const JPEG_QUALITY = 0.9;

/**
 * Generates a multi-page PDF directly from the already-rendered certificate iframe.
 * Each `.em_main_table.watermark_wrapper` table inside the iframe is captured as
 * one PDF page via html2canvas. Falls back to the full body if no wrappers found.
 *
 * Because the iframe uses srcDoc (same-origin), html2canvas can read computed
 * styles and layout directly from the iframe's document — no offscreen cloning needed.
 */
export async function generateAtolCertificatePdfFromIframe(iframe: HTMLIFrameElement, filename: string): Promise<void> {
  const doc = iframe.contentDocument;
  if (!doc) {
    throw new Error('Cannot access iframe content');
  }

  const wrapperTables = doc.querySelectorAll('table.em_main_table.watermark_wrapper');
  const elements: Array<HTMLElement> =
    wrapperTables.length > 0 ? (Array.from(wrapperTables) as Array<HTMLElement>) : [doc.body];

  const { default: html2canvas } = await import('html2canvas');
  const { default: jsPDF } = await import('jspdf');

  const pdf = new jsPDF('p', 'mm', 'a4');

  for (let i = 0; i < elements.length; i++) {
    const canvas = await html2canvas(elements[i], { scale: CANVAS_SCALE, useCORS: true });
    const imgData = canvas.toDataURL('image/jpeg', JPEG_QUALITY);

    const naturalWidth = A4_WIDTH_MM;
    const naturalHeight = (canvas.height * naturalWidth) / canvas.width;
    const scale = Math.min(1, A4_HEIGHT_MM / naturalHeight);
    const finalWidth = naturalWidth * scale;
    const finalHeight = naturalHeight * scale;
    const xOffset = (A4_WIDTH_MM - finalWidth) / 2;

    if (i > 0) {
      pdf.addPage();
    }

    pdf.setFillColor(...ATOL_BACKGROUND_RGB);
    pdf.rect(0, 0, A4_WIDTH_MM, A4_HEIGHT_MM, 'F');
    pdf.addImage(imgData, 'JPEG', xOffset, 0, finalWidth, finalHeight);
  }

  pdf.save(filename);
}
